/**
 * Where the widget UI must stay out of the way (PLAN.md §3.4).
 *
 * Login / register / password pages are the default: the panel would cover the form,
 * and anything typed there is exactly what we never want a reporter to screenshot.
 * Capture keeps running — only the launcher, panel and announcement band are withheld.
 */

/** Login, logout, sign-up, password and verification routes — TR and EN spellings. */
export const AUTH_PATHS: readonly string[] = [
  '/login',
  '/log-in',
  '/signin',
  '/sign-in',
  '/logout',
  '/signout',
  '/sign-out',
  '/register',
  '/signup',
  '/sign-up',
  '/password/*',
  '/forgot-password',
  '/reset-password',
  '/confirm-password',
  '/verify-email',
  '/email/verify/*',
  '/two-factor-challenge',
  '/2fa',
  '/otp',
  '/auth/*',
  '/oauth/*',
  '/sso/*',
  '/giris',
  '/giris-yap',
  '/oturum-ac',
  '/uye-girisi',
  '/kullanici-girisi',
  '/cikis',
  '/cikis-yap',
  '/kayit',
  '/kayit-ol',
  '/uye-ol',
  '/sifremi-unuttum',
  '/sifre-sifirla',
  '/sifre-yenile',
  '/parola-sifirla',
  '/wp-login.php',
  '/login.php',
  '/giris.php',
];

const cache = new Map<string, RegExp | null>();

/**
 * `/login` matches `/login`, `/tr/login` and `/admin/login` but not `/login-history`;
 * `/password/*` matches the whole subtree; `*` inside a segment matches that segment only.
 */
function toRegExp(pattern: string): RegExp | null {
  const hit = cache.get(pattern);
  if (hit !== undefined) return hit;

  let p = pattern.trim().toLowerCase().replace(/\/+$/, '');
  if (p === '') return cache.set(pattern, null), null;
  if (!p.startsWith('/')) p = '/' + p;

  const subtree = p.endsWith('/*');
  const body = (subtree ? p.slice(0, -2) : p)
    .slice(1)
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '[^/]*');
  const re = new RegExp(`(?:^|/)${body}${subtree ? '(?:/.*)?' : ''}/?$`);
  cache.set(pattern, re);

  return re;
}

/** Path candidates one URL should be tested against, including `#/login` hash routes. */
function paths(url: string): string[] {
  let path = url;
  let search = '';
  let hash = '';
  try {
    const u = new URL(url, typeof location !== 'undefined' ? location.href : 'http://localhost/');
    path = u.pathname;
    search = u.search;
    hash = u.hash;
  } catch {
    /* not a URL — treat the whole string as a path */
  }
  path = path.toLowerCase();
  const out = [path, path + search.toLowerCase()];
  if (hash.startsWith('#/')) out.push(hash.slice(1).toLowerCase());

  return out;
}

export interface VisibilityRules {
  /** Hide on the built-in auth routes (default on). */
  authPages: boolean;
  /** Extra patterns from the dashboard or the script tag. */
  paths?: readonly string[];
}

/** Should the widget UI stay hidden on this URL? */
export function isHiddenUrl(url: string, rules: VisibilityRules): boolean {
  const patterns = [...(rules.authPages ? AUTH_PATHS : []), ...(rules.paths ?? [])];
  if (patterns.length === 0) return false;
  const candidates = paths(url);

  return patterns.some((pattern) => {
    const re = toRegExp(pattern);

    return re !== null && candidates.some((c) => re.test(c));
  });
}
